import { useState } from 'react';
import { 
  viewFacturationPdf, 
  downloadFacturationPdf, 
  sendFacturationByEmail,
  generateFacturationPdf 
} from '../../service/api/facturationService';
import { toast } from 'sonner';
import { Tooltip } from "react-tooltip";
import { useTranslation } from 'react-i18next';

/**
 * Composant réutilisable pour les actions PDF
 */
const PdfActions = ({ facturation, onActionComplete, domainId }) => {
  const { t } = useTranslation(['facturation', 'common']);
  const [loadingAction, setLoadingAction] = useState(null);

  const handleViewPdf = async () => {
    if (!facturation?.id) {
      toast.error(t('validation.errors.invalidInvoice'));
      return;
    }

    setLoadingAction('view');
    
    try {
      const result = await viewFacturationPdf(facturation.id);
      toast.success(result.message || t('pdfActions.view.success'));
      
      onActionComplete?.('view');
      
    } catch (error) {
      console.error('View PDF Error:', error);
      
      if (error.message.includes('non trouvé') || error.message.includes('404')) {
        toast.error(t('pdfActions.notFound'));
        
        setTimeout(() => handleGenerateAndViewPdf(), 1000);
      } else {
        toast.error(error.message || t('errors.viewPdf'));
      }
    } finally {
      setLoadingAction(null);
    }
  };

  const handleDownloadPdf = async () => {
    if (!facturation?.id) {
      toast.error(t('validation.errors.invalidInvoice'));
      return;
    }

    setLoadingAction('download');
    
    try {
      const result = await downloadFacturationPdf(facturation.id);
      toast.success(result.message || t('pdfActions.download.success'));
      
      onActionComplete?.('download');
      
    } catch (error) {
      console.error('Download PDF Error:', error);
      
      if (error.message.includes('non trouvé')) {
        toast.error(t('pdfActions.notFound'));
        setTimeout(() => handleGenerateAndDownloadPdf(), 1000);
      } else {
        toast.error(error.message || t('errors.downloadPdf'));
      }
    } finally {
      setLoadingAction(null);
    }
  };

  const handleSendEmail = async (useSecureLink = true) => {
    if (!facturation?.id) {
      toast.error(t('validation.errors.invalidInvoice'));
      return;
    }

    setLoadingAction('email');
    
    try {
      const result = await sendFacturationByEmail(facturation.id, useSecureLink);
      
      if (result.success) {
        toast.success(result.message || t('pdfActions.email.success'));
        onActionComplete?.('email');
      } else {
        toast.error(result.message || t('errors.sendEmail'));
      }
      
    } catch (error) {
      console.error('Send Email Error:', error);
      toast.error(error.message || t('errors.sendEmail'));
    } finally {
      setLoadingAction(null);
    }
  };

  const handleGenerateAndViewPdf = async () => {
    if (!facturation?.id || !domainId) return;
    
    try {
      await generateFacturationPdf(facturation.id, domainId);
      toast.success(t('pdfActions.generated'));
      
      setTimeout(() => handleViewPdf(), 1500);
    } catch (error) {
      toast.error(t('errors.generatePdf'));
    }
  };

  const handleGenerateAndDownloadPdf = async () => {
    if (!facturation?.id || !domainId) return;
    
    try {
      await generateFacturationPdf(facturation.id, domainId);
      toast.success(t('pdfActions.generated'));
      
      setTimeout(() => handleDownloadPdf(), 1500);
    } catch (error) {
      toast.error(t('errors.generatePdf'));
    }
  };

  return (
    <>
      <div className="btn-group pdf-actions-group" role="group" aria-label={t('pdfActions.title')}>
        
        {/* BOUTON AFFICHER PDF */}
        <button
          className="btn btn-outline-info btn-sm"
          onClick={handleViewPdf}
          disabled={loadingAction === 'view'}
          data-tooltip-id="tooltip-view-pdf"
          data-tooltip-content={t('pdfActions.view.tooltip')}
          aria-label={t('pdfActions.view.label')}
        >
          {loadingAction === 'view' ? (
            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
          ) : (
            <i className="fas fa-eye" aria-hidden="true"></i>
          )}
        </button>

        {/* BOUTON TÉLÉCHARGER PDF */}
        <button
          className="btn btn-outline-success btn-sm"
          onClick={handleDownloadPdf}
          disabled={loadingAction === 'download'}
          data-tooltip-id="tooltip-download-pdf"
          data-tooltip-content={t('pdfActions.download.tooltip')}
          aria-label={t('pdfActions.download.label')}
        >
          {loadingAction === 'download' ? (
            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
          ) : (
            <i className="fas fa-download" aria-hidden="true"></i>
          )}
        </button>

        {/* MENU ENVOYER EMAIL */}
        <div className="btn-group" role="group">
          <button
            className="btn btn-outline-primary btn-sm dropdown-toggle"
            type="button"
            id={`emailDropdown-${facturation.id}`} 
            data-bs-toggle="dropdown"
            aria-expanded="false"
            disabled={loadingAction === 'email'}
            data-tooltip-id="tooltip-email-pdf"
            data-tooltip-content={t('pdfActions.email.tooltip')}
            aria-label={t('pdfActions.email.label')}
          >
            {loadingAction === 'email' ? (
              <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
            ) : (
              <i className="fas fa-paper-plane" aria-hidden="true"></i>
            )}
          </button>
  
          
          <ul 
            className="dropdown-menu dropdown-menu-end"
            aria-labelledby={`emailDropdown-${facturation.id}`} 
            style={{ 
              position: 'fixed',
              zIndex: 999999, 
              maxHeight: '200px',
              overflowY: 'auto'
            }}
          >
            <li>
              <button 
                className="dropdown-item d-flex align-items-center"
                style={{ zIndex:1000000 }}
                onClick={() => handleSendEmail(true)}
                disabled={loadingAction === 'email'}
              >
                <i className="fas fa-link text-info me-2" aria-hidden="true"></i>
                <div>
                  <div className="fw-semibold">{t('pdfActions.email.options.secureLink.label')}</div>
                  <small className="text-muted">{t('pdfActions.email.options.secureLink.description')}</small>
                </div>
              </button>
            </li>
            <li><hr className="dropdown-divider"/></li>
            <li>
              <button
                className="dropdown-item d-flex align-items-center"
                style={{ zIndex:100000 }}
                onClick={() => handleSendEmail(false)}
                disabled={loadingAction === 'email'}
              >
                <i className="fas fa-paperclip text-warning me-2" aria-hidden="true"></i>
                <div>
                  <div className="fw-semibold">{t('pdfActions.email.options.attachment.label')}</div>
                  <small className="text-muted">{t('pdfActions.email.options.attachment.description')}</small>
                </div>
              </button>
            </li>
          </ul>
        </div>
      </div>

      {/* TOOLTIPS ACCESSIBLES */}
      <Tooltip id="tooltip-view-pdf" place="top" />
      <Tooltip id="tooltip-download-pdf" place="top" />
      <Tooltip id="tooltip-email-pdf" place="top" />
    </>
  );
};

export default PdfActions;